'use client';

/**
 * Composes the morfi-side bindings (Zustand stores, toast, share, telemetry,
 * i18n, currency formatter, network) into a `TradeMutationDeps` value and
 * supplies it to the SDK's `<TradingHostProvider>`. The SDK trading hooks
 * (`useBuyTrade`, `useSellTrade`, `useSwapTrade`, `useBoostTrade`,
 * `useAddThesis`, `useClaimReceipt`) read this via `useTradingHostContext()`.
 */
import { c as _c } from "react/compiler-runtime";
import { reconcilePositionsAfterTrade, type ShareTradeRequest as SdkShareTradeRequest, TradingHostProvider as SdkTradingHostProvider, type TradeMutationDeps, type TxStage, useAuth, useQueryClient, useTurnkeySubmissionDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { readSellDeeplinkEntryPoint } from '@/components/market/use-sell-deeplink-preset';
import { getUsdcMint } from '@/config/solana';
import { onEvent } from '@/events';
import type { TradeChartMetric } from '@/events/trading';
import { prepareConfirmedPositionUpdate } from '@/hooks/positions/user-portfolio/confirmed-position-transitions';
import { isTradeChartMetric, readTradeChartMetric } from '@/lib/analytics/trade-chart-context';
import { tradeLifecycleProperties } from '@/lib/analytics/trade-lifecycle';
import { useFormattedCurrency } from '@/lib/currency';
import { formatShares } from '@/lib/format/format-shares';
import { getActiveNetwork } from '@/lib/network/active';
import { buildTradeShareUrl, openShareModal } from '@/lib/share';
import { reportError, trackAction } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';
import { getConnection } from '@/lib/tx-engine/connection';
import { useSubmitTransaction } from '@/lib/tx-engine/use-submit-transaction';
import { fireRewardBurst } from '@/lib/ui/confetti';
import { dismissToast, showToast } from '@/lib/ui/toast';
import { useInvalidateWalletBalances } from '@/queries/wallet-balance';
import { useLocalThesesStore } from '@/stores/local-theses';
import { useThesisDialogStore } from '@/stores/thesis-dialog';
import { recordAttemptEvent, recordAttemptSignature, useTradeAttemptJournal } from '@/stores/trade-attempt-journal';
import { useTxSubmissionStore } from '@/stores/tx-submission';
function getExplorerUrl(signature: string): string {
  const network = getActiveNetwork();
  if (network === 'mainnet') {
    return `https://explorer.solana.com/tx/${signature}`;
  }
  return `https://explorer.solana.com/tx/${signature}?cluster=${network}`;
}
export function TradingHostProvider(t0) {
  const $ = _c(42);
  const {
    children
  } = t0;
  const submitTx = useSubmitTransaction();
  const {
    user
  } = useAuth();
  const userId = user?.id ?? null;
  const turnkeyDeps = useTurnkeySubmissionDeps();
  const queryClient = useQueryClient();
  const invalidateWalletBalances = useInvalidateWalletBalances();
  const t = useTranslations("tx");
  const {
    formatBalance,
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const txSubmission = useTxSubmissionStore();
  let t1;
  if ($[0] !== t) {
    t1 = Object.assign(t, {
      format: t
    });
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1 as TradeMutationDeps['t'];
  let t3;
  if ($[2] !== formatD6) {
    t3 = d6 => formatD6(d6);
    $[2] = formatD6;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== formatD6Signed) {
    t4 = d6_1 => formatD6Signed(d6_1);
    $[4] = formatD6Signed;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  if ($[6] !== userId) {
    t5 = (name, payload) => {
      const chartMetricAtSubmission = name === "trade_attempted" ? readTradeChartMetric(typeof payload.marketAddress === "string" ? payload.marketAddress : undefined) : name === "trade_success" ? readRecordedTradeChartMetric(typeof payload.traceId === "string" ? payload.traceId : undefined, userId) : undefined;
      const attributed = chartMetricAtSubmission === undefined ? payload : {
        ...payload,
        chartMetricAtSubmission
      };
      if (!recordAttemptEvent(name, attributed, userId)) {
        return;
      }
      const enriched = name === "trade_success" ? {
        ...attributed,
        ...tradeLifecycleProperties(userId)
      } : attributed;
      onEvent(name as Parameters<typeof onEvent>[0], enriched as never);
    };
    t6 = submission => {
      recordAttemptEvent("trade_submission", submission, userId);
    };
    $[6] = userId;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      readEntryPoint: readSellDeeplinkEntryPoint
    };
    t8 = {
      getBaseUrl: _temp4,
      buildUrl: _temp5,
      openModal: _temp6
    };
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  let t10;
  let t9;
  if ($[11] !== txSubmission) {
    t9 = (traceId, stage, extra) => {
      txSubmission.updateStage(traceId, stage as TxStage, extra);
    };
    t10 = (traceId_0, signature_0) => {
      recordAttemptSignature(traceId_0, signature_0);
      txSubmission.setSignature(traceId_0, signature_0);
    };
    $[11] = txSubmission;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9 || $[16] !== txSubmission.setError || $[17] !== txSubmission.startSubmission) {
    t11 = {
      startSubmission: txSubmission.startSubmission,
      updateStage: t9,
      setSignature: t10,
      setError: txSubmission.setError
    };
    $[14] = t10;
    $[15] = t9;
    $[16] = txSubmission.setError;
    $[17] = txSubmission.startSubmission;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      openDialog: _temp7,
      reconcileDialogSignature: _temp8,
      addLocal: _temp9
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== invalidateWalletBalances || $[21] !== queryClient) {
    t13 = kind => {
      invalidateWalletBalances();
      if (kind === "buy" || kind === "sell" || kind === "swap") {
        reconcilePositionsAfterTrade(queryClient);
      } else {
        if (kind === "claim_prize") {
          reconcilePositionsAfterTrade(queryClient);
        }
      }
    };
    $[20] = invalidateWalletBalances;
    $[21] = queryClient;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== queryClient || $[24] !== userId) {
    t14 = {
      prepare: () => prepareConfirmedPositionUpdate(queryClient, userId ?? "")
    };
    $[23] = queryClient;
    $[24] = userId;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== formatBalance || $[27] !== queryClient || $[28] !== submitTx || $[29] !== t11 || $[30] !== t13 || $[31] !== t14 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== turnkeyDeps) {
    t15 = {
      submitTx,
      turnkeyDeps,
      queryClient,
      getConnection,
      t: t2,
      formatBalance,
      formatBalanceD6: t3,
      formatShareD6: _temp,
      formatBalanceSigned: t4,
      showToast,
      dismissToast,
      getExplorerUrl,
      openExternalUrl: _temp2,
      emitEvent: t5,
      recordTradeSubmission: t6,
      trackAction,
      reportError: _temp3,
      logger,
      getActiveNetwork,
      getUsdcMint,
      sellDeeplink: t7,
      tradeSharing: t8,
      txSubmission: t11,
      thesis: t12,
      onMutationSettled: t13,
      onClaimConfirmed: _temp10,
      optimisticPositions: t14
    };
    $[26] = formatBalance;
    $[27] = queryClient;
    $[28] = submitTx;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = turnkeyDeps;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const value = t15;
  let t16;
  if ($[39] !== children || $[40] !== value) {
    t16 = <SdkTradingHostProvider value={value}>{children}</SdkTradingHostProvider>;
    $[39] = children;
    $[40] = value;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}
function _temp10() {
  fireRewardBurst();
}
function _temp9(signature_3, content) {
  useLocalThesesStore.getState().addLocalThesis(signature_3, content);
}
function _temp8(tradeId_0, signature_2) {
  useThesisDialogStore.getState().reconcileDialogSignature(tradeId_0, signature_2);
}
function _temp7(marketAddress, signature_1, walletAddress, tradeContext, tradeId) {
  useThesisDialogStore.getState().openDialog(marketAddress, signature_1, walletAddress, tradeContext, tradeId);
}
function _temp6(request) {
  openShareModal(request as unknown as Parameters<typeof openShareModal>[0]);
}
function _temp5(signature, ctx_0) {
  return buildTradeShareUrl(signature, ctx_0);
}
function _temp4() {
  return typeof window !== "undefined" ? window.location.origin : "";
}
function _temp3(err, ctx) {
  const stringCtx = {};
  for (const [k, v] of Object.entries(ctx)) {
    stringCtx[k] = String(v);
  }
  reportError(err, stringCtx);
}
function _temp2(url) {
  if (typeof window !== "undefined") {
    window.open(url, "_blank", "noopener");
  }
}
function _temp(d6_0) {
  return formatShares(d6_0);
}
function readRecordedTradeChartMetric(traceId: string | undefined, userId: string | null): TradeChartMetric {
  if (traceId === undefined || userId === null) return 'unknown';
  const attempt = useTradeAttemptJournal.getState().attempts[traceId];
  if (attempt?.userId !== userId) return 'unknown';
  const metric = attempt.properties.chartMetricAtSubmission;
  return typeof metric === 'string' && isTradeChartMetric(metric) ? metric : 'unknown';
}